/**
 * Short tones for the PHONE side camera.
 *
 * The phone is propped aside during the exam and its screen is not being
 * watched, so a purely visual recording indicator is not enough: the student
 * needs to *hear* that recording started, and hear it if it drops. These are
 * generated with the Web Audio API — no asset to bundle, works offline.
 *
 * Mobile browsers refuse to play audio until the AudioContext has been resumed
 * inside a user gesture, so `unlockBeeps()` must be called from a real tap once
 * (the "Enable camera" tap does it), after which the tones are allowed to play.
 */

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const AC =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AC) return null;
  if (!ctx) {
    try {
      ctx = new AC();
    } catch {
      return null;
    }
  }
  return ctx;
}

/**
 * Resume the audio context from within a user gesture so later beeps may play.
 * Safe to call repeatedly and from any tap.
 */
export function unlockBeeps(): void {
  const c = getCtx();
  if (c && c.state === "suspended") void c.resume();
}

function tone(
  c: AudioContext,
  freq: number,
  startOffsetS: number,
  durationS: number,
  gain: number,
): void {
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  osc.connect(g);
  g.connect(c.destination);

  const t = c.currentTime + startOffsetS;
  // A tiny attack/release envelope so the tone does not click on/off.
  g.gain.setValueAtTime(0, t);
  g.gain.linearRampToValueAtTime(gain, t + 0.012);
  g.gain.setValueAtTime(gain, t + durationS - 0.02);
  g.gain.linearRampToValueAtTime(0, t + durationS);

  osc.start(t);
  osc.stop(t + durationS + 0.02);
}

/** Two ascending notes — "recording is live". Reassuring, not alarming. */
export function beepConfirm(): void {
  const c = getCtx();
  if (!c) return;
  void c.resume();
  tone(c, 660, 0, 0.13, 0.16);
  tone(c, 880, 0.15, 0.18, 0.16);
}

/** Low descending double buzz — "recording dropped, look at the phone". */
export function beepError(): void {
  const c = getCtx();
  if (!c) return;
  void c.resume();
  tone(c, 320, 0, 0.22, 0.22);
  tone(c, 240, 0.26, 0.32, 0.22);
}
